import React, { useState, useEffect } from 'react';
import { Search, Plus, Eye, EyeOff, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // ✅ Import useNavigate
import { getData, postData, serverURL } from '../../../Services/Fetchnodeservices';

export default function RestaurantDashboard() {
  const navigate = useNavigate(); // ✅ Initialize navigate
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [visibleRows, setVisibleRows] = useState({});
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuLoading, setMenuLoading] = useState(null);
  const [stats, setStats] = useState({
    totalMenus: 0,
    totalItems: 0,
    activeMenus: 0,
    pending: 0
  });

  // Fetch restaurants data
  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getData("Menu/get_restaurant_settings");
      console.log("=== RESTAURANT SETTINGS RESPONSE ===");
      console.log("Full Response:", response);
      
      if (response && response.status) {
        console.log("✅ Successfully fetched restaurant settings");
        console.log("📊 Data Count:", response.data?.length || 0);
        console.log("📝 Data:", response.data);
        
        if (response.data && response.data.length > 0) {
          const transformedData = response.data.map(item => ({
            id: item.id,
            name: item.organisation_name || 'N/A',
            contact: item.contact || 'N/A',
            items: item.item_count || 0,
            direction: getDirectionText(item),
            commission: getCommissionText(item),
            status: Number(item.status) === 1 ? "Active" : Number(item.status) === 0 ? "Inactive" : "",
            is_menu: item.is_menu || 0,
            updated: item.updated_at ? new Date(item.updated_at).toLocaleString() : 'N/A',
            menu_direction: item.menu_direction,
            menu_direction_type: item.menu_direction_type,
            menu_direction_value: item.menu_direction_value,
            comission: item.comission,
            special_comission: item.special_comission
          }));
          
          setRestaurants(transformedData);
          
          const totalMenus = transformedData.length;
          const totalItems = transformedData.reduce((sum, item) => sum + item.items, 0);
          const activeMenus = transformedData.filter(item => item.status === 'Active').length;
          const pending = transformedData.filter(item => item.status === 'Inactive').length;
          
          setStats({
            totalMenus,
            totalItems,
            activeMenus,
            pending
          });
          
          console.log("📊 Transformed Data:", transformedData);
        } else {
          console.log("⚠️ No restaurant data found");
          setRestaurants([]);
          setStats({
            totalMenus: 0,
            totalItems: 0,
            activeMenus: 0,
            pending: 0
          });
        }
      } else {
        console.log("❌ Failed to fetch restaurant settings");
        console.log("Error Message:", response?.message || 'Unknown error');
        setError(response?.message || 'Failed to fetch data');
        setRestaurants([]);
      }
    } catch (error) {
      console.error("❌ Error fetching restaurant settings:", error);
      console.error("Error Details:", error.message);
      if (error.response) {
        console.error("Server Response:", error.response.data);
      }
      setError(error.message || 'Server error while fetching data');
      setRestaurants([]);
    } finally {
      setLoading(false);
      console.log("=== END OF FETCH ===");
    }
  };

  const getDirectionText = (item) => {
    if (!item.is_menu || item.is_menu === 0) {
      return 'Neutral';
    }
    if (item.menu_direction_type === 'Percentage') {
      return `${item.menu_direction_value || 0}% - Percentage`;
    }
    return item.menu_direction || 'Neutral';
  };

  const getCommissionText = (item) => {
    const comission = item.comission || 0;
    const specialComission = item.special_comission || 0;
    return `${comission}% (Sp: ${specialComission}%)`;
  };

  // ✅ Handle menu toggle with Optimistic Update
  const handleMenuToggle = async (id, currentStatus) => {
    const currentNum = Number(currentStatus);
    const newStatus = currentNum === 1 ? 0 : 1;
    
    console.log(`🔄 Toggling: ID ${id}, ${currentNum} -> ${newStatus}`);
    
    // Optimistic update
    setRestaurants(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, is_menu: newStatus, _updating: true }
          : item
      )
    );
    
    setMenuLoading(id);
    
    try {
      const response = await postData("Menu/change_menu_status", {
        id: id,
        is_menu: newStatus
      });
      
      console.log("📥 Response:", response);
      
      if (response && response.status === true) {
        setRestaurants(prev => 
          prev.map(item => 
            item.id === id 
              ? { ...item, is_menu: newStatus, _updating: false }
              : item
          )
        );
        
        await fetchRestaurants();
        console.log(`✅ Menu ${newStatus === 1 ? 'Enabled' : 'Disabled'}`);
      } else {
        console.log("❌ Failed, reverting...");
        setRestaurants(prev => 
          prev.map(item => 
            item.id === id 
              ? { ...item, is_menu: currentNum, _updating: false }
              : item
          )
        );
        alert(response?.message || 'Failed to change menu status');
      }
    } catch (error) {
      console.error("❌ Error:", error);
      setRestaurants(prev => 
        prev.map(item => 
          item.id === id 
            ? { ...item, is_menu: currentNum, _updating: false }
            : item
        )
      );
      alert('Server error while changing menu status');
    } finally {
      setMenuLoading(null);
    }
  };

  // ✅ NEW: Handle View Navigation with Data
  const handleViewRestaurant = (restaurant) => {
    console.log("👀 Navigating to menu with:", {
      designation_id: restaurant.id,
      restaurantName: restaurant.name
    });
    
    // Navigate to Dashboard/menus with state
    navigate('/Dashboard/Menus', {
      state: {
        designation_id: restaurant.id,
        restaurantName: restaurant.name,
        // Additional data if needed
        restaurantData: restaurant
      }
    });
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.contact.includes(searchTerm) ||
                         restaurant.id.toString().includes(searchTerm);
    const matchesStatus = statusFilter === 'All' || restaurant.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const StatCard = ({ icon, label, value }) => (
    <div style={{
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
      padding: '24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px'
    }}>
      <div style={{ fontSize: '32px' }}>{icon}</div>
      <div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>{value}</div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>{label}</div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader className="animate-spin" size={48} color="#3B82F6" style={{ margin: '0 auto 16px', display: 'block' }} />
          <p style={{ color: '#6b7280' }}>Loading restaurants...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          backgroundColor: '#ffffff',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
          maxWidth: '448px'
        }}>
          <div style={{ color: '#ef4444', fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>Error Loading Data</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={fetchRestaurants}
            style={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: '600',
              padding: '8px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <StatCard icon="🏪" label="Total Menus" value={stats.totalMenus} />
          <StatCard icon="🍽️" label="Total Items" value={stats.totalItems} />
          <StatCard icon="📈" label="Active Menus" value={stats.activeMenus} />
          <StatCard icon="⏰" label="Pending" value={stats.pending} />
        </div>

        {/* Action Button */}
        <div style={{ marginBottom: '24px' }}>
          <button style={{
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            fontWeight: '600',
            padding: '8px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            <Plus size={20} />
            Initialize Menu
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              flex: 1,
              position: 'relative',
              width: '100%'
            }}>
              <Search style={{
                position: 'absolute',
                left: '12px',
                top: '12px',
                color: '#9ca3af'
              }} size={20} />
              <input
                type="text"
                placeholder="Search by restaurant name, ID or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 16px 8px 40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  outline: 'none',
                  fontSize: '14px'
                }}
                onFocus={(e) => e.target.style.outline = '2px solid #3b82f6'}
                onBlur={(e) => e.target.style.outline = 'none'}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['All', 'Active', 'Inactive'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontWeight: '500',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: statusFilter === status ? '#3b82f6' : '#e5e7eb',
                    color: statusFilter === status ? '#ffffff' : '#374151'
                  }}
                  onMouseEnter={(e) => {
                    if (statusFilter !== status) {
                      e.target.style.backgroundColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (statusFilter !== status) {
                      e.target.style.backgroundColor = '#e5e7eb';
                    }
                  }}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Restaurant Table */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '12px 24px', textAlign: 'left' }}>
                    <input type="checkbox" style={{ borderRadius: '4px' }} />
                  </th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>ID</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Pic</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Restaurant</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Contact</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Items</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Direction</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Commission</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Menu</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Status</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Updated</th>
                  <th style={{ padding: '12px 24px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRestaurants.length > 0 ? (
                  filteredRestaurants.map((restaurant, idx) => {
                    const isUpdating = restaurant._updating || menuLoading === restaurant.id;
                    return (
                      <tr key={restaurant.id} style={{ 
                        backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                        opacity: isUpdating ? 0.7 : 1,
                        transition: 'opacity 0.3s ease'
                      }}>
                        <td style={{ padding: '16px 24px' }}>
                          <input type="checkbox" style={{ borderRadius: '4px' }} />
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>{restaurant.id}</td>
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            background: 'linear-gradient(to bottom right, #99f6e4, #5eead4)',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px'
                          }}>
                            🍽️
                          </div>
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', fontWeight: '500', color: '#111827' }}>{restaurant.name}</td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', color: '#6b7280' }}>{restaurant.contact}</td>
                        <td style={{ padding: '16px 24px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '9999px',
                            fontSize: '14px',
                            fontWeight: '600',
                            backgroundColor: restaurant.items > 0 ? '#ccfbf1' : '#f3f4f6',
                            color: restaurant.items > 0 ? '#065f46' : '#6b7280'
                          }}>
                            {restaurant.items}
                          </span>
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px' }}>
                          {restaurant.items === 0 ? (
                            <span style={{ color: '#6b7280' }}>− Neutral</span>
                          ) : (
                            <span style={{ color: '#0d9488', fontWeight: '500' }}>↑ {restaurant.direction}</span>
                          )}
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', color: '#6b7280' }}>{restaurant.commission}</td>
                        
                        {/* Menu Toggle */}
                        <td style={{ padding: '16px 24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button
                              onClick={() => handleMenuToggle(restaurant.id, restaurant.is_menu)}
                              disabled={isUpdating}
                              aria-label={`Toggle menu for ${restaurant.name}`}
                              style={{
                                position: 'relative',
                                width: '56px',
                                height: '28px',
                                borderRadius: '14px',
                                border: 'none',
                                cursor: isUpdating ? 'not-allowed' : 'pointer',
                                background: restaurant.is_menu === 1 ? '#1D9E75' : '#d1d5db',
                                transition: 'background 0.25s ease',
                                outline: `0.5px solid ${restaurant.is_menu === 1 ? '#0F6E56' : '#b0b3ba'}`,
                                padding: 0,
                                opacity: isUpdating ? 0.55 : 1,
                                flexShrink: 0
                              }}
                            >
                              <span
                                style={{
                                  position: 'absolute',
                                  top: '3px',
                                  left: '3px',
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '50%',
                                  background: '#ffffff',
                                  transform: restaurant.is_menu === 1 ? 'translateX(28px)' : 'translateX(0)',
                                  transition: 'transform 0.25s ease',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px'
                                }}
                              >
                                {isUpdating
                                  ? <Loader size={13} className="animate-spin" color="#6b7280" />
                                  : restaurant.is_menu === 1
                                    ? <span style={{ color: '#1D9E75', fontWeight: 700, lineHeight: 1 }}>✓</span>
                                    : <span style={{ color: '#9ca3af', fontWeight: 700, lineHeight: 1 }}>✕</span>
                                }
                              </span>
                            </button>

                            <span style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              minWidth: '28px',
                              color: isUpdating ? '#9ca3af' : restaurant.is_menu === 1 ? '#1D9E75' : '#9ca3af',
                              transition: 'color 0.2s'
                            }}>
                              {isUpdating ? '...' : restaurant.is_menu === 1 ? 'ON' : 'OFF'}
                            </span>
                          </div>
                        </td>
                        
                        <td style={{ padding: '16px 24px' }}>
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 12px',
                            borderRadius: '9999px',
                            fontSize: '14px',
                            fontWeight: '600',
                            backgroundColor: restaurant.status === 'Active' ? '#dcfce7' : '#fee2e2',
                            color: restaurant.status === 'Active' ? '#166534' : '#991b1b'
                          }}>
                            {restaurant.status}
                          </span>
                        </td>
                        <td style={{ padding: '16px 24px', fontSize: '14px', color: '#6b7280' }}>{restaurant.updated}</td>
                        
                        {/* ✅ UPDATED: View Button with Navigation */}
                        <td style={{ padding: '16px 24px' }}>
                          <button
                            onClick={() => handleViewRestaurant(restaurant)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#3b82f6',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '14px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#eff6ff';
                              e.target.style.color = '#2563eb';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.color = '#3b82f6';
                            }}
                          >
                            <Eye size={18} />
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="12" style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
                      <div style={{ fontSize: '40px', marginBottom: '8px' }}>📭</div>
                      <p>No restaurants found</p>
                      <p style={{ fontSize: '14px', marginTop: '4px' }}>Try adjusting your search or filters</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results Count */}
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#6b7280' }}>
          Showing {filteredRestaurants.length} of {restaurants.length} restaurants
        </div>
      </div>
    </div>
  );
}